import React, { useState } from "react";

import { useDispatch } from "react-redux";

import type { BusbudError } from "@busbud/busbud-errors/lib/errors";
import Alert from "@busbud/design-system-components/dist/Alert";
import CheckboxLabel from "@busbud/design-system-components/dist/CheckboxLabel";
import CircularProgress from "@busbud/design-system-components/dist/CircularProgress/CircularProgress";

import { useAppContext } from "@app/helpers/hooks";
import {
  privacyUrl,
  termsOfUseUrl
} from "@app/services/url-builders/company-pages-url-builder";
import { clickedEmailOptInCheckbox } from "@app/tracking/account-tracking";
import type { NapiUser } from "@app/types/napi-types";
import type { SigningMethod } from "@app/types/tracking/account";

import { Carousel } from "./carousel";
import { ContinueWithEmail } from "./continue-with-email";
import { useCarouselItems } from "./use-carousel-items";
import { getErrorText } from "../../../../modules/account/utils";
import { logUserIn } from "../../../../modules/results/actions";
import { Markdown } from "../../../text-formatting/markdown";
import { useAnimation } from "../authentication.styles";
import { useStyles } from "../sign-in-up.styles";
import { SocialConnectButtons } from "../social-buttons/social-connect-buttons";

interface Props {
  email?: string;
  goToSignIn: () => void;
  redirect_url?: string;
  carousel_start_idx?: number;
}

export const SignUp: React.FC<Props> = ({
  email,
  goToSignIn,
  redirect_url,
  carousel_start_idx
}) => {
  const { translator, geoData, user_country_code, tracker, locale } =
    useAppContext();
  const [isInProgress, setInProgress] = useState(false);
  const auto_enrol_new_user =
    geoData.allowsImplicitConsentForMarketingEmails(user_country_code);
  const [optin, setOptin] = useState(auto_enrol_new_user);
  const { classes: animation_classes } = useAnimation();
  const { classes, cx } = useStyles();
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const carousel_items = useCarouselItems();

  const handleSocialSignUpError = (err: BusbudError) => {
    const { response } = err;
    const error_type: string =
      response &&
      response.data &&
      response.data.error &&
      response.data.error.type;
    setError(getErrorText(translator, error_type));
  };

  const onSuccess = (user: NapiUser, sign_in_method?: SigningMethod) => {
    dispatch(logUserIn({ user, redirect_url, sign_in_method }));
  };

  const onOptInCheckboxChange = () => {
    const new_optin_value = !optin;
    setOptin(new_optin_value);
    tracker.track(clickedEmailOptInCheckbox(new_optin_value));
  };

  return (
    <div
      className={cx(
        "relative flex flex-col items-center gap-150",
        animation_classes.container
      )}
    >
      <Carousel items={carousel_items} start_index={carousel_start_idx} />

      {!!error && <Alert severity="negative">{error}</Alert>}
      <div>
        {!!isInProgress && (
          <div className={classes.backdrop}>
            <CircularProgress size="lg" />
          </div>
        )}
      </div>
      <div className="mt-200 flex flex-col gap-200">
        <SocialConnectButtons
          setInProgress={setInProgress}
          onSuccess={onSuccess}
          onError={handleSocialSignUpError}
          email_opt_in={optin}
        />
        <ContinueWithEmail
          email={email}
          goToSignIn={goToSignIn}
          redirect_url={redirect_url}
          email_opt_in={optin}
        />
        {!auto_enrol_new_user && (
          <CheckboxLabel
            classes={{ root: classes.optin }}
            label={translator.t("!account.sign-up.opt_in")}
            variant="text"
            checked={optin}
            onChange={onOptInCheckboxChange}
          />
        )}
        <Markdown
          className={cx(
            classes.termsAndCondition,
            "text-center text-color-secondary"
          )}
          body_font_size="sm"
          link_target_blank
          content={translator.t("!account.sign-up.terms-conditions", {
            terms_of_use_link: termsOfUseUrl(locale),
            privacy_policy_link: privacyUrl(locale)
          })}
        />
      </div>
    </div>
  );
};
